import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TeamEditor = ({ content, onSave }) => {
  const [formData, setFormData] = useState({
    section_label: "",
    title: "",
    role: "",
    description: "",
    team_image: "",
    highlights: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) {
      setFormData(content);
    }
  }, [content]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHighlightChange = (index, value) => {
    const updated = [...formData.highlights];
    updated[index] = value;
    setFormData({ ...formData, highlights: updated });
  };

  const addHighlight = () => {
    setFormData({ ...formData, highlights: [...formData.highlights, "Nuevo punto destacado"] });
  };

  const removeHighlight = (index) => {
    const updated = formData.highlights.filter((_, i) => i !== index);
    setFormData({ ...formData, highlights: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const success = await onSave(formData);
    setSaving(false);
    if (success) {
      toast.success("Equipo actualizado correctamente");
    } else {
      toast.error("Error al guardar los cambios");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="team-editor"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Editar Equipo</h1>
          <p className="text-[#94a3b8]">Modifica la información del equipo</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Información Principal</h2>
          <div className="grid gap-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Etiqueta</label>
                <input
                  type="text"
                  name="section_label"
                  value={formData.section_label}
                  onChange={handleChange}
                  className="contact-input w-full"
                />
              </div>
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Cargo/Rol</label>
                <input
                  type="text"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="contact-input w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Título</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="contact-input w-full"
              />
            </div>
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Descripción</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="contact-input w-full resize-none"
              />
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Imagen del Equipo</h2>
          <div>
            <label className="block text-[#94a3b8] text-sm mb-2">URL de la imagen</label>
            <div className="flex gap-4">
              <input
                type="url"
                name="team_image"
                value={formData.team_image}
                onChange={handleChange}
                className="contact-input flex-1"
                placeholder="https://..."
              />
              <div className="w-32 h-20 bg-[#0b1221] border border-white/10 overflow-hidden">
                {formData.team_image && (
                  <img src={formData.team_image} alt="Preview" className="w-full h-full object-cover" />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Puntos Destacados</h2>
            <button type="button" onClick={addHighlight} className="btn-secondary flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" />
              <span>Agregar</span>
            </button>
          </div>
          <div className="space-y-3">
            {formData.highlights.map((highlight, index) => (
              <div key={index} className="flex items-center gap-3">
                <input
                  type="text"
                  value={highlight}
                  onChange={(e) => handleHighlightChange(index, e.target.value)}
                  className="contact-input flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeHighlight(index)}
                  className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span>{saving ? "Guardando..." : "Guardar Cambios"}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default TeamEditor;
